import { defineStore } from 'pinia';
import {
  login as userLogin,
  loginByPhone as userLoginByPhone,
  logout as userLogout,
  getUserInfo,
  LoginData,
} from '@/api/user';
import { Modal } from '@arco-design/web-vue';
import { setToken, clearToken, setUserId, getUserId } from '@/utils/auth';
import { removeRouteListener } from '@/utils/route-listener';
import { UserState } from './types';
import useAppStore from '../app';

const PLATFORM_NAME_STORAGE_PREFIX = 'zscd-platform-name';

function getPlatformNameStorageKey(userId?: string | number) {
  return `${PLATFORM_NAME_STORAGE_PREFIX}:${userId || 'default'}`;
}

function getLocalPlatformName(userId?: string | number) {
  return localStorage.getItem(getPlatformNameStorageKey(userId)) || '';
}

const useUserStore = defineStore('user', {
  state: (): UserState => ({
    name: undefined,
    username: undefined,
    avatar: undefined,
    job: undefined,
    organization: undefined,
    location: undefined,
    email: undefined,
    introduction: undefined,
    personalWebsite: undefined,
    jobName: undefined,
    organizationName: undefined,
    locationName: undefined,
    phone: undefined,
    registrationDate: undefined,
    accountId: undefined,
    certification: undefined,
    isAdmin: 0,
    role: '',
    platformName: undefined,
    hasUnsettledElectricityBills: undefined,
    partnerId: undefined,
  }),

  getters: {
    userInfo(state: UserState): UserState {
      return { ...state };
    },
  },

  actions: {
    switchRoles() {
      return new Promise((resolve) => {
        this.role = this.role === 'user' ? 'admin' : 'user';
        resolve(this.role);
      });
    },
    // Set user's information
    setInfo(partial: Partial<UserState>) {
      this.$patch(partial);
    },

    // Reset user's information
    resetInfo() {
      this.$reset();
    },

    // Get user's information
    async info() {
      try {
        console.log('获取用户信息...');
        const res = await getUserInfo();
        console.log('用户信息响应:', res);

        // 处理后端响应
        let userData = null;

        if (res.data) {
          // 如果后端返回的是标准格式 { code: 200, data: {...}, msg: 'success' }
          const responseData = res.data as any;
          if (responseData.code === 200 && responseData.data) {
            userData = responseData.data;
          } else {
            userData = res.data;
          }

          // 设置用户信息
          const userId = userData.id || '';
          const localPlatformName = getLocalPlatformName(userId);
          this.setInfo({
            name: userData.nickName || userData.username || userData.name,
            username: userData.username || userData.name,
            avatar: userData.avatar,
            accountId: String(userId),
            email: userData.email,
            phone: userData.mobile || userData.phone,
            role: userData.role || 'user',
            isAdmin: userData.isAdmin,
            platformName: localPlatformName || userData.platformName,
            hasUnsettledElectricityBills: userData.hasUnsettledElectricityBills,
            partnerId: userData.partnerId,
          });

          // 保存 userId (使用之前的 remember 状态是做不到的，因为这里没有 access 到 loginForm.remember)
          // 我们可以尝试从 localStorage/sessionStorage 推断 remember 状态
          const isRemembered = !!localStorage.getItem('token');
          if (userId) {
            setUserId(String(userId), isRemembered);
          }

          // 检查是否有未结算的电费账单
          if (userData.hasUnsettledElectricityBills) {
            // 延迟显示提示，确保页面已加载完成
            setTimeout(() => {
              this.showUnsettledBillsNotification();
            }, 1000);
          }
        }

        return userData;
      } catch (error) {
        console.error('获取用户信息失败:', error);
        throw error;
      }
    },

    // Login
    async login(loginForm: LoginData & { rememberPassword?: boolean }) {
      try {
        console.log('登录请求数据:', loginForm);
        const res = await userLogin(loginForm);
        return this.handleLoginResponse(res, loginForm.rememberPassword);
      } catch (error) {
        console.error('登录失败:', error);
        throw error;
      }
    },

    // Login by phone
    async loginByPhone(loginForm: {
      mobile: string;
      code: string;
      rememberPassword?: boolean;
    }) {
      try {
        console.log('手机登录请求数据:', loginForm);
        const res = await userLoginByPhone({
          mobile: loginForm.mobile,
          code: loginForm.code,
        });
        return this.handleLoginResponse(res, loginForm.rememberPassword);
      } catch (error) {
        console.error('手机登录失败:', error);
        throw error;
      }
    },

    // Handle login response
    async handleLoginResponse(res: any, rememberPassword?: boolean) {
      console.log('登录响应:', res);

      // 处理不同的响应格式
      let token = '';
      let refreshToken = '';

      // 检查响应格式
      const data = res.data as any;
      console.log('原始响应数据:', data);
      if (data) {
        if (data.code && data.code !== 200 && data.code !== 20000) {
          throw new Error(data.message || data.msg || '登录失败');
        }
        // 优先匹配新接口结构
        // 格式: { accessToken: '...', refreshToken: '...', ... } 或 { code: 200, data: { accessToken: '...' } }
        const targetData = data.code === 200 && data.data ? data.data : data;
        console.log('处理后的目标数据:', targetData);
        if (targetData) {
          token = targetData.accessToken || targetData.token;
          refreshToken = targetData.refreshToken || '';
        }

        // 兼容旧逻辑兜底
        if (!token) {
          if (typeof targetData === 'string') {
            token = targetData;
          } else if (typeof data === 'string') {
            token = data;
          }
        }
      }

      if (!token) {
        console.error('无法从响应中获取token:', res);
        throw new Error('登录失败：无法获取访问令牌');
      }

      // 保存token
      // 默认为 true (与表单默认值保持一致)，或者由调用方传入
      const remember = rememberPassword ?? true;
      setToken(token, refreshToken, remember);

      // 获取用户信息
      await this.info();

      // 获取应用配置
      const appStore = useAppStore();
      if (typeof appStore.fetchServerMenuConfig === 'function') {
        console.log('开始获取菜单数据...');
        appStore.fetchServerMenuConfig();
      }

      return true;
    },

    logoutCallBack() {
      const appStore = useAppStore();
      this.resetInfo();
      clearToken();
      removeRouteListener();
      appStore.clearServerMenu();
    },

    // 显示未结算电费账单通知
    showUnsettledBillsNotification() {
      // 检查当前是否在费用与账单页面，如果是则不显示弹窗
      const currentPath = window.location.pathname;
      if (currentPath.includes('/billing/electricity')) {
        console.log('当前在费用与账单页面，不显示弹窗提醒');
        return;
      }

      Modal.warning({
        title: '电费账单提醒',
        content:
          '您有未结算的电费账单需要处理，建议及时前往费用与账单页面进行充值处理。',
        okText: '立即前往',
        cancelText: '稍后处理',
        onOk: () => {
          // 使用window.location跳转，因为store中无法直接使用router
          window.location.href = '/billing/electricity';
        },
        onCancel: () => {
          // 用户选择稍后处理，不做任何操作
        },
      });
    },
    updateLocalPlatformName(platformName: string) {
      const nextPlatformName = platformName.trim();
      localStorage.setItem(
        getPlatformNameStorageKey(this.accountId),
        nextPlatformName
      );
      this.platformName = nextPlatformName;
    },
    // Logout
    async logout() {
      try {
        const userId = getUserId();
        await userLogout(userId || undefined, 'web');
      } finally {
        this.logoutCallBack();
      }
    },
  },
});

export default useUserStore;
